import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Tournament } from '../../_shared/models/tournament.model';
import { Observable, Subject, lastValueFrom, map } from 'rxjs';
import { BoardHelpService } from '../../match-tracker/tracker-board/board-help.service';
import { AuthService } from '../../auth/auth.service';

@Injectable({ providedIn: 'root' })
export class TournamentDetailsService {
    tournament: Tournament;
    private selectedTournamentDate: string;
    private liveScoreObservable$ = new Subject<any>();
    private bhs = inject(BoardHelpService);
    private authService = inject(AuthService);
    private updateTournamentAfterToggleAllowFansToScore$ = new Subject<boolean>();

    constructor(private httpClient: HttpClient) { }

    getTournament(tournamentId: string, skipCache: boolean) {
        return this.httpClient.post<any>('api_endpoint' + `/tournament/${tournamentId}/info`, { skipCache }).pipe(
            map(res => res.data)
        )
    }

    async getTournamentInfo(tournamentId: string, forceToFetch: boolean = false) {
        if (!this.tournament || this.tournament?._id !== tournamentId || forceToFetch) {
            const response = await lastValueFrom(this.httpClient.post<any>('api_endpoint' + `/tournament/${tournamentId}/info`, { }));
            this.tournament = response.data;
            this.bhs.isUserFan = this.tournament?.viewers?.some(follower => follower === this.authService.authUserSignal()?._id);
            this.bhs.totalFansCount = this.tournament?.viewers?.length;
            this.bhs.allowFansToScore = this.tournament?.allowFansToScore || false;
        }
        return this.tournament;
    }

    getMatches(tournamentId: string, matchUpStatusType: string, filter, skipCache: boolean) {
        return this.httpClient.post<any>('api_endpoint' + `/match/tournament-matches/${tournamentId}/${matchUpStatusType}`, { filter, skipCache, userId: this.authService.authUserSignal()?._id });
    }

    sendStandings(_tournamentId: string, standings: any, partner: string) {
        return this.httpClient.post<any>('api_endpoint' + `/partner/send-tournament-standings`, { _tournamentId, standings, partner });
    }

    addTracker(tournamentId: string, trackerIds: string[] = [], isInvited?: boolean, phone?: string, email?: string) {
        return this.httpClient.put<any>('api_endpoint' + `/tournament/add-tracker`, { tournamentId, trackerIds, isInvited, phone, email });
    }

    removeTracker(tournamentId: string, trackerId: string) {
        return this.httpClient.put<any>('api_endpoint' + `/tournament/remove-tracker`, { tournamentId, trackerId });
    }

    follow(tournamentId: string) {
        return this.httpClient.post<any>('api_endpoint' + `/tournament/follow`, { tournamentId });
    }

    unfollow(tournamentId: string) {
        return this.httpClient.post<any>('api_endpoint' + `/tournament/unfollow`, { tournamentId });
    }

    syncWithSpreadsheet(tournamentId: string) {
        return this.httpClient.post<any>('api_endpoint' + `/tournament/${tournamentId}/spreadsheet-sync`, { });
    }

    allowResultSubmissionToPartner(tournamentId: string, allowResultSubmissionToPartner: boolean) {
        return this.httpClient.post<any>('api_endpoint' + `/tournament/${tournamentId}/allowResultSubmissionToPartner`, { allowResultSubmissionToPartner });
    }

    updateWidgetStatus(tournamentId: string, isWidgetEnabled: boolean) {
        return this.httpClient.post<any>('api_endpoint' + `/tournament/${tournamentId}/widget`, { isWidgetEnabled });
    }

    allowAutoSubmitScore(tournamentId: string, allowAutoSubmitScore: boolean) {
        return this.httpClient.post<any>('api_endpoint' + `/tournament/${tournamentId}/allowAutoSubmitScore`, { allowAutoSubmitScore });
    }

    publishClearBadge(tab: string) {
        this.liveScoreObservable$.next(tab);
    }

    subscribeClearBadge(): Observable<any> {
        return this.liveScoreObservable$.asObservable();
    }

    setSelectedTournamentDate(date: string): void {
        this.selectedTournamentDate = date;
    }

    getSelectedTournamentDate(): string {
        return this.selectedTournamentDate ?? '';
    }

    updateTournamentAfterToggleAllowFansToScore(allowFansToScore: boolean) {
        this.updateTournamentAfterToggleAllowFansToScore$.next(allowFansToScore);
    }

    getTournamentAfterToggleAllowFansToScore() {
        return this.updateTournamentAfterToggleAllowFansToScore$.asObservable();
    }

    getDrawMatches(tournamentId: string, eventId: string) {
        return this.httpClient.get<any>('api_endpoint' + `/tournament/${tournamentId}/event/${eventId}`);
    }

    updateAnnouncement(tournamentId: string, message: string, enabled: boolean) {
        return this.httpClient.put<any>('api_endpoint' + `/tournament/${tournamentId}/announcement`, { message, enabled });
    }

    updateNotification(tournamentId: string, message: string, enabled: boolean, textUpperCase: boolean, color: string) {
        return this.httpClient.put<any>('api_endpoint' + `/tournament/${tournamentId}/notification`, { message, enabled, textUpperCase, color });
    }

}
